import React, { useMemo } from 'react';

import { Button, FontAwesomeIcon } from '@appcharge/shared-ui';
import { RollingOfferProductSequence } from 'common/contracts';
import { motion } from 'framer-motion';
import { TabsProps } from 'views/RollingOffers/types';

import DialogModal from 'components/DialogModal/DialogModal';
import { cn } from 'components/lib/utils';
import { Collapsible, CollapsibleTrigger } from 'components/ui/collapsible';

import CardContent from './CardContent';

interface SubOfferCardProps extends TabsProps {
  productSequenceItem: RollingOfferProductSequence;
  sequenceIndex: number;
  isExpanded: boolean;
  isCurrentItemDragged?: boolean;
  onToggleExpand: () => void;
  onDelete: () => void;
  dragHandleListeners?: any;
  dragHandleAttributes?: any;
  setBarPointsValidity?: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
}

const SubOfferCard: React.FC<SubOfferCardProps> = ({
  formikProps,
  productSequenceItem,
  sequenceIndex,
  setProductsSequence,
  setBarPointsValidity,
  isExpanded,
  isCurrentItemDragged = false,
  onToggleExpand,
  onDelete,
  dragHandleListeners,
  dragHandleAttributes
}) => {
  const { setFieldValue, handleChange, values } = formikProps;
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  const collapseIcon = useMemo(
    () => (isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'),
    [isExpanded]
  );

  const cardTitle = `Sub Offer #${sequenceIndex + 1}`;

  const handleOpenChange = (open: boolean) => {
    if (open !== isExpanded) {
      onToggleExpand();
    }
  };

  const updateSubOfferData = (
    updatedData: Partial<RollingOfferProductSequence>,
    e?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setProductsSequence((prevSequence: RollingOfferProductSequence[]) => {
      const updatedSequence = [...prevSequence];
      updatedSequence[sequenceIndex] = {
        ...updatedSequence[sequenceIndex],
        ...updatedData
      };
      setFieldValue('productsSequence', updatedSequence);
      return updatedSequence;
    });
    if (e) handleChange(e);
  };

  const handleClickDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDeleteDialogOpen(true);
  };

  return (
    <div className="flex flex-col">
      <Collapsible
        className={cn('group/collapsible')}
        open={isExpanded}
        onOpenChange={handleOpenChange}
      >
        <div
          className={cn(
            'flex flex-col border rounded-lg transition-colors bg-white border-base-sidebarBorder hover:border-indigo-500'
          )}
        >
          <CollapsibleTrigger className="group flex justify-between items-center w-full h-[61px] px-4 text-base-mutedForeground text-xs">
            <div
              className={cn(
                'leading-5 font-sans font-medium flex items-center gap-3',
                isExpanded && 'text-indigo-600'
              )}
            >
              <span
                {...(dragHandleListeners ? dragHandleListeners : {})}
                {...(dragHandleAttributes ? dragHandleAttributes : {})}
                className={cn(
                  'inline-flex items-center',
                  'cursor-grab active:cursor-grabbing'
                )}
              >
                <FontAwesomeIcon
                  icon="fa-solid fa-grip-dots-vertical"
                  className={cn(
                    'text-md transition-opacity w-[12px] text-base-mutedForeground group-hover/collapsible:text-base-foreground',
                    (isCurrentItemDragged || isExpanded) &&
                      'text-base-foreground'
                  )}
                />
              </span>
              <span className="text-base-foreground font-medium text-sm">
                {cardTitle}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={handleClickDelete}
                className={cn(
                  'p-1 rounded opacity-0 group-hover/collapsible:opacity-100 hover:bg-gray-50 w-10 h-10 flex items-center justify-center transition-opacity shadow-none'
                )}
                title="Delete sub offer"
              >
                <FontAwesomeIcon
                  icon="fa-regular fa-trash-can"
                  className="text-sm text-base-foreground"
                />
              </Button>
              <FontAwesomeIcon
                icon={`fa-solid ${collapseIcon}`}
                className={cn(
                  'text-xs text-base-mutedForeground h-10 w-10 flex items-center justify-center'
                )}
              />
            </div>
          </CollapsibleTrigger>

          <motion.div
            layout
            initial={{ height: 0 }}
            animate={{ height: isExpanded ? 'auto' : 0 }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <CardContent
              productSequenceItem={productSequenceItem}
              sequenceIndex={sequenceIndex}
              offerName={values.name || ''}
              updateSubOfferData={updateSubOfferData}
              setBarPointsValidity={setBarPointsValidity}
            />
          </motion.div>
        </div>
      </Collapsible>

      {isDeleteDialogOpen && (
        <DialogModal
          isOpen={isDeleteDialogOpen}
          dataTestId="SubOfferCard-DeleteModal"
          onOpenChange={(open: boolean) => {
            if (!open) setIsDeleteDialogOpen(false);
          }}
          title="Delete Sub Offer"
          description="Are you sure you want to delete this sub offer? This action can't be undone."
          modalActions={{
            onConfirm: {
              label: 'Delete',
              action: () => {
                onDelete();
                setIsDeleteDialogOpen(false);
              }
            },
            onClose: {
              label: 'Cancel',
              action: () => setIsDeleteDialogOpen(false),
              variant: 'link'
            }
          }}
        />
      )}
    </div>
  );
};

export default SubOfferCard;
